import {
  CameraInterfaceApplyResult,
  CameraInterfaceConflict,
  CameraInterfaceProposal,
  ProposedCameraInterface,
  ServiceActionResult,
  SkippedPredicate,
  SystemOverview,
  SystemOverviewCounts,
} from '@/api/systemAnalyzerApi/types.gen'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string')

const isOneOf = <T extends string>(value: unknown, options: readonly T[]): value is T =>
  typeof value === 'string' && (options as readonly string[]).includes(value)

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === 'string'

const isSystemOverviewCounts = (value: unknown): value is SystemOverviewCounts =>
  isRecord(value) &&
  (['ok', 'error', 'warning', 'info', 'skipped', 'total'] as const).every(key => typeof value[key] === 'number')

const isSkippedPredicate = (value: unknown): value is SkippedPredicate =>
  isRecord(value) && typeof value.name === 'string' && isStringArray(value.depends_on)

export const isSystemOverview = (value: unknown): value is SystemOverview =>
  isRecord(value) &&
  isOneOf(value.status, ['ok', 'degraded', 'error'] as const) &&
  isSystemOverviewCounts(value.counts) &&
  isStringArray(value.error_predicates) &&
  isStringArray(value.warning_predicates) &&
  isStringArray(value.info_predicates) &&
  Array.isArray(value.skipped_predicates) &&
  value.skipped_predicates.every(isSkippedPredicate)

const isProposedCameraInterface = (value: unknown): value is ProposedCameraInterface =>
  isRecord(value) &&
  typeof value.user_defined_name === 'string' &&
  typeof value.camera_ip === 'string' &&
  isNullableString(value.name) &&
  isNullableString(value.iface) &&
  isNullableString(value.mac) &&
  isOneOf(value.status, ['ok', 'rename', 'conflict', 'unresolved'] as const) &&
  typeof value.detail === 'string'

const isCameraInterfaceConflict = (value: unknown): value is CameraInterfaceConflict =>
  isRecord(value) &&
  isOneOf(value.reason, ['duplicate_name', 'shared_iface'] as const) &&
  Array.isArray(value.entries) &&
  value.entries.every(isProposedCameraInterface)

export const isCameraInterfaceProposal = (value: unknown): value is CameraInterfaceProposal =>
  isRecord(value) &&
  Array.isArray(value.cameras) &&
  value.cameras.every(isRecord) &&
  Array.isArray(value.proposed) &&
  value.proposed.every(isProposedCameraInterface) &&
  Array.isArray(value.conflicts) &&
  value.conflicts.every(isCameraInterfaceConflict) &&
  isNullableString(value.error)

export const isCameraInterfaceApplyResult = (value: unknown): value is CameraInterfaceApplyResult => {
  if (!isRecord(value) || !isCameraInterfaceProposal(value)) return false
  const { applied, apply_error } = value as Record<string, unknown>
  return (
    (applied === undefined || (Array.isArray(applied) && applied.every(isProposedCameraInterface))) &&
    (apply_error === undefined || typeof apply_error === 'string')
  )
}

export const isServiceActionResult = (value: unknown): value is ServiceActionResult =>
  isRecord(value) && isOneOf(value.status, ['success', 'error'] as const) && typeof value.message === 'string'

export const isLogText = (value: unknown): value is string => typeof value === 'string'
